import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trophy, Lock, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function Avatar({ hue, initials }) {
    const bg = `hsl(${hue}, 55%, 65%)`;
    return (
        <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-[11px] shrink-0"
            style={{ backgroundColor: bg }}
        >
            {initials}
        </div>
    );
}

function initialsOf(pseudonyme) {
    const parts = (pseudonyme || "").split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function LeaderboardTab() {
    const [optedIn, setOptedIn] = useState(null);   // null while loading
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data: status } = await api.get("/leaderboard/opt-in-status");
                setOptedIn(!!status.opted_in);
                if (status.opted_in) {
                    const { data: board } = await api.get("/leaderboard/current");
                    setData(board);
                } else {
                    const { data: scale } = await api.get("/leaderboard/scale");
                    setData({ scale: scale.scale, window_days: scale.window?.days || 7 });
                }
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    async function toggleOptIn(nextValue) {
        setBusy(true);
        try {
            await api.post("/leaderboard/opt-in", { opted_in: nextValue });
            setOptedIn(nextValue);
            if (nextValue) {
                const { data: board } = await api.get("/leaderboard/current");
                setData(board);
                toast.success("Bienvenue dans le classement — bonne chance cette semaine !");
            } else {
                const { data: scale } = await api.get("/leaderboard/scale");
                setData({ scale: scale.scale, window_days: scale.window?.days || 7 });
                toast.success("Classement désactivé — votre position n'est plus visible.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    if (loading || optedIn === null) {
        return (
            <div className="flex items-center justify-center py-12" data-testid="leaderboard-loading">
                <Loader2 className="w-5 h-5 animate-spin text-terracotta" />
            </div>
        );
    }

    return (
        <div data-testid="leaderboard-tab">
            {/* Opt-in toggle card */}
            <div className="bg-white border border-lexiBorder rounded-2xl p-4 mb-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-terracotta/12 flex items-center justify-center shrink-0">
                        <Trophy className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">
                            Classement hebdomadaire · 7 jours glissants
                        </p>
                        <p className="font-serif text-lg mt-0.5">
                            {optedIn ? "Vous êtes dans le classement." : "Rejoignez le classement de la semaine."}
                        </p>
                        <p className="text-[11px] text-inkSoft mt-1 max-w-md">
                            Optionnel et respectueux. Vous êtes toujours affichée sous pseudonyme (prénom + initiale).
                            Aucune donnée sensible n&apos;est publiée.
                        </p>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="leaderboard-toggle">
                        <input
                            type="checkbox"
                            checked={optedIn}
                            onChange={(e) => toggleOptIn(e.target.checked)}
                            disabled={busy}
                            className="w-4 h-4 accent-terracotta"
                            data-testid="leaderboard-optin-checkbox"
                        />
                        <span className="text-xs text-inkSoft">
                            {optedIn ? "Activé" : "Désactivé"}
                        </span>
                    </label>
                </div>
            </div>

            {/* Not opted in → show scale + empty state */}
            {!optedIn ? (
                <div className="bg-paperAlt border border-lexiBorder rounded-2xl p-6" data-testid="leaderboard-empty">
                    <div className="flex items-start gap-3">
                        <Lock className="w-4 h-4 text-inkSoft mt-0.5" />
                        <div>
                            <p className="font-serif text-base">
                                Activez le classement pour vous mesurer, gentiment, à d&apos;autres apprenants.
                            </p>
                            <p className="text-xs text-inkSoft mt-2">
                                Voici le barème pour situer votre effort d&apos;une semaine :
                            </p>
                        </div>
                    </div>
                    <ul className="mt-4 space-y-2" data-testid="scale-list">
                        {(data?.scale || []).map((s, i) => (
                            <li key={i} className="flex items-center justify-between text-sm bg-white border border-lexiBorder rounded-xl px-3 py-2">
                                <span className="text-ink">{s.label_fr}</span>
                                <span className="font-mono font-semibold text-terracotta">+{s.points} pts</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <>
                    <ul className="space-y-2" data-testid="leaderboard-top">
                        {(data?.top || []).map((e) => {
                            const isYou = !!e.is_you;
                            return (
                                <li
                                    key={`${e.rank}-${e.pseudonyme}`}
                                    className={`flex items-center gap-3 rounded-2xl border px-3 py-2 ${isYou
                                        ? "bg-terracotta/10 border-terracotta"
                                        : "bg-white border-lexiBorder"}`}
                                    data-testid={`leaderboard-rank-${e.rank}`}
                                >
                                    <span className="text-[11px] tracking-[0.22em] uppercase text-inkSoft w-8">
                                        {String(e.rank).padStart(2, "0")}
                                    </span>
                                    <Avatar hue={e.avatar_hue || 200} initials={initialsOf(e.pseudonyme)} />
                                    <span className="flex-1 font-serif text-base truncate">
                                        {e.pseudonyme}
                                        {isYou && (
                                            <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-terracotta">
                                                Vous
                                            </span>
                                        )}
                                    </span>
                                    <span className="font-mono font-semibold text-ink">
                                        {e.score} <span className="text-[10px] text-inkSoft">pts</span>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>

                    {data?.you && !(data?.top || []).some((e) => e.is_you) && (
                        <div
                            className="mt-4 rounded-2xl border border-terracotta bg-terracotta/10 px-3 py-2 flex items-center gap-3"
                            data-testid="leaderboard-you"
                        >
                            <span className="text-[11px] tracking-[0.22em] uppercase text-terracotta w-8">
                                {String(data.you.rank).padStart(2, "0")}
                            </span>
                            <Avatar hue={data.you.avatar_hue || 200} initials={initialsOf(data.you.pseudonyme)} />
                            <span className="flex-1 font-serif text-base truncate">
                                {data.you.pseudonyme}
                                <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-terracotta">
                                    Vous
                                </span>
                            </span>
                            <span className="font-mono font-semibold text-ink">
                                {data.you.score} <span className="text-[10px] text-inkSoft">pts</span>
                            </span>
                        </div>
                    )}

                    {/* Scale collapsed at the bottom for transparency */}
                    <details className="mt-6 group" data-testid="leaderboard-scale-details">
                        <summary className="cursor-pointer flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-inkSoft">
                            <Info className="w-3 h-3" /> Comment sont calculés les points ?
                        </summary>
                        <ul className="mt-3 space-y-1.5">
                            {(data?.scale || []).map((s, i) => (
                                <li key={i} className="flex items-center justify-between text-xs bg-paperAlt border border-lexiBorder rounded-lg px-3 py-1.5">
                                    <span className="text-ink">{s.label_fr}</span>
                                    <span className="font-mono font-semibold text-terracotta">+{s.points} pts</span>
                                </li>
                            ))}
                        </ul>
                    </details>
                </>
            )}
        </div>
    );
}
